import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';

@Component({
  selector: 'app-support-details',
  templateUrl: './support-details.component.html',
  styleUrls: ['./support-details.component.scss'],
})
export class SupportDetailsComponent implements OnInit {
  helpSupportList: any[] = [];
  userList: any[] = [];
  totalDataCount = 0;
  pageSize = 12;
  pageIndex = 1;
  loading = false;
  skipIndex = 0;

  // Filters
  search = '';
  selectedQueryType: string | null = null;
  selectedUser: string | null = null;
  dateRange: Date[] | null = null;

  searchRequest: Subject<string> = new Subject<string>();

  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
  ) {}

  ngOnInit(): void {
    this.searchRequest.pipe(debounceTime(1000)).subscribe((value: string) => {
      this.search = value;
      this.resetPagination();
      this.getAllHelpSupport();
    });

    this.getAllUsers();
    this.getAllHelpSupport();
  }

  /* Build query params */
  private buildParams(sortBy?: any): any {
    const params: any = {
      skip: this.skipIndex,
      limit: this.pageSize,
    };

    if (this.search) params.search = this.search;
    if (this.selectedQueryType) params.queryType = this.selectedQueryType;
    if (this.selectedUser) params.user = this.selectedUser;

    if (this.dateRange && this.dateRange.length === 2) {
      params.startDate = this.dateRange[0].toISOString();
      params.endDate = this.dateRange[1].toISOString();
    }

    if (sortBy) params.sortBy = JSON.stringify(sortBy);

    return params;
  }

  /* Get all help support requests */
  getAllHelpSupport(sortBy?: any): void {
    const params = this.buildParams(sortBy);
    this.loading = true;

    this.httpRequestService
      .request('get', 'users/support-details', params)
      .subscribe(
        (result) => {
          this.loading = false;
          this.helpSupportList = result.data;
          this.totalDataCount = result.totalCount;
        },
        () => {
          this.loading = false;
        },
      );
  }

  /* Get all users for filter dropdown */
  getAllUsers(): void {
    this.httpRequestService
      .request('get', 'users', { skip: 0, limit: 500 })
      .subscribe((result) => {
        this.userList = result.data || [];
      });
  }

  /* Pagination handler */
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex, sort } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);

    const currentSort = sort.find((s) => s.value !== null);
    const sortBy = currentSort
      ? { [currentSort.key]: currentSort.value === 'ascend' ? 1 : -1 }
      : undefined;

    this.getAllHelpSupport(sortBy);
  }

  /* Filter handlers */
  onQueryTypeChange(value: string): void {
    this.resetPagination();
    this.getAllHelpSupport();
  }

  onUserChange(value: string): void {
    this.resetPagination();
    this.getAllHelpSupport();
  }

  onDateRangeChange(dates: Date[]): void {
    this.dateRange = dates;
    this.resetPagination();
    this.getAllHelpSupport();
  }

  searchHelpSupport(event: any): void {
    this.searchRequest.next(event);
  }

  /* Update status */
  updateStatus(id: string, status: 'in_progress' | 'resolved'): void {
    this.httpRequestService
      .request('put', `users/help-support/${id}/status`, { status })
      .subscribe(
        () => {
          this.notificationService.success(
            '',
            `Request marked as ${status.replace('_', ' ')} successfully`,
          );
          this.getAllHelpSupport();
        },
        (error) => {
          this.notificationService.error(
            '',
            error?.error?.message || 'Failed to update status',
          );
        },
      );
  }

  /* Delete request */
  // deleteRequest(id: string): void {
  //   this.httpRequestService
  //     .request('delete', `user/help-support/${id}`)
  //     .subscribe(
  //       () => {
  //         this.notificationService.success('', 'Request deleted successfully');
  //         this.getAllHelpSupport();
  //       },
  //       (error) => {
  //         this.notificationService.error(
  //           '',
  //           error?.error?.message || 'Failed to delete request',
  //         );
  //       },
  //     );
  // }

  /* Helper: reset pagination */
  private resetPagination(): void {
    this.pageIndex = 1;
    this.skipIndex = 0;
  }

  /* Helper: query type display label */
  getQueryTypeLabel(type: string): string {
    const map: Record<string, string> = {
      subscription_plan: 'Subscription Plan',
      payment_issue: 'Payment Issue',
      app_bug: 'App Bug',
      other: 'Other',
    };
    return map[type] || type;
  }

  /* Helper: query type color */
  getQueryTypeColor(type: string): string {
    const map: Record<string, string> = {
      subscription_plan: 'blue',
      payment_issue: 'orange',
      app_bug: 'red',
      other: 'default',
    };
    return map[type] || 'default';
  }

  /* Helper: status display label */
  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
    };
    return map[status] || status;
  }

  /* Helper: status color */
  getStatusColor(status: string): string {
    const map: Record<string, string> = {
      open: 'volcano',
      in_progress: 'gold',
      resolved: 'green',
    };
    return map[status] || 'default';
  }
}
